// src/components/EnhancedQuoteCard.jsx
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Copy, Brain } from 'lucide-react';
import ClaimRealityCard from './ClaimRealityCard';
import DemocracyThreatCard from './DemocracyThreatCard';
import AuthoritarianPlaybook from './AuthoritarianPlaybook';
import HistoricalPrecedents from './HistoricalPrecedents';
import DemocracyTimeline from './DemocracyTimeline';
import DemocracyIndexQuoteContext from './DemocracyIndexQuoteContext';
import DemocracyInsights from './DemocracyInsights';
import CardContainer from './CardContainer';
import ClaudeAnalysisForm from './ClaudeAnalysisForm';
import { MobileButton } from './mobile';

const EnhancedQuoteCard = ({ quote, defaultExpanded = false, onTagClick }) => {
  const [frameworkData, setFrameworkData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysisExpanded, setAnalysisExpanded] = useState(defaultExpanded);
  const [activeTab, setActiveTab] = useState('threats');
  const [copySuccess, setCopySuccess] = useState(false);
  const [storedAnalysis, setStoredAnalysis] = useState(null);
  
  // Fetch framework data specific to this quote
  useEffect(() => {
    // Only fetch if quote has an ID
    if (quote.id) {
      fetchFrameworkData(quote.id);
      
      // Try to retrieve stored analysis from sessionStorage for newly created quotes
      try {
        const savedAnalysis = sessionStorage.getItem('lastClaudeAnalysis');
        if (savedAnalysis) {
          setStoredAnalysis(JSON.parse(savedAnalysis));
          // Clear after retrieving to avoid using it for different quotes
          sessionStorage.removeItem('lastClaudeAnalysis');
        }
      } catch (e) {
        console.warn('Error retrieving stored analysis:', e);
      }
    }
  }, [quote.id]);
  
  const fetchFrameworkData = async (quoteId) => {
    setLoading(true);
    try {
      // First try the main framework endpoint
      const response = await fetch(`/api/quotes/${quoteId}/framework`);
      if (response.ok) {
        const data = await response.json();
        console.log("Framework data fetched for quote", quoteId, data);
        
        // If we have no data, try the debug endpoint as a fallback
        if ((!data.pillars || data.pillars.length === 0) && 
            (!data.tactics || data.tactics.length === 0) && 
            (!data.precedents || data.precedents.length === 0)) {
          console.log("No framework data found, trying debug endpoint...");
          
          try {
            const debugResponse = await fetch(`/api/admin/quote-debug?id=${quoteId}`);
            if (debugResponse.ok) {
              const debugData = await debugResponse.json();
              console.log("Debug data:", debugData);
              
              // If debug data shows we have connections in the database, use that
              if (debugData.pillars.length > 0 || 
                  debugData.tactics.length > 0 || 
                  debugData.precedents.length > 0) {
                
                // Create a new framework data object using the raw data
                const fixedData = {
                  ...data,
                  pillars: debugData.pillars,
                  tactics: debugData.tactics,
                  precedents: debugData.precedents,
                  indices: debugData.indices
                };
                console.log("Using fixed data from debug endpoint:", fixedData);
                setFrameworkData(fixedData);
                return;
              }
            }
          } catch (debugError) {
            console.error("Error fetching debug data:", debugError);
          }
        }
        
        setFrameworkData(data);
      } else {
        throw new Error('Failed to fetch framework data');
      }
    } catch (error) {
      console.error("Error fetching framework data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate slug from quote text - moved outside handleCopyLink to be used by View Full Analysis link
  const generateSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 60)
      .replace(/-+$/, ''); // Remove trailing dashes
  };
  
  // Handle copy link to clipboard (retained but not used now)
  const handleCopyLink = () => {
    // Use new slug-based URL structure
    const slug = generateSlug(quote.claim_text || quote.text || '');
    const url = `${window.location.origin}/q/${quote.id}/${slug}.html`;
    
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => console.error('Failed to copy link: ', err));
  };

  // Determine if we have any framework data to show
  // We want to show the analysis button even if there's only quote data
  // This ensures we always show the "View Analysis" button for quotes with reality text
  const hasFrameworkData = frameworkData && quote && quote.reality_text;

  // Create a modified quote object with the onTagClick function for ClaimRealityCard
  const enhancedQuote = {
    ...quote,
    onTagClick: onTagClick // This ensures the ClaimRealityCard has access to the tag click functionality
  };

  // Detect if screen is mobile size
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Footer component
  const footer = (
    <div className={`space-y-3`}>
      {/* Tags section in organized grid layout */}
      {quote.tags && quote.tags.length > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <div className="mb-2">
            <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Tags</span>
          </div>
          <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-4'} gap-2`}>
            {quote.tags.map((tag, index) => (
              <span 
                key={index} 
                className="text-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md cursor-pointer hover:bg-gray-200 truncate mobile-touch-target"
                onClick={() => onTagClick && onTagClick(tag)}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* View Full Analysis button */}
      <div className={`flex justify-end border-t border-gray-200 pt-3`}>
        <a 
          href={`${window.location.origin}/q/${quote.id}/${generateSlug(quote.claim_text || quote.text || '')}.html`}
          className={`flex items-center px-3 py-2 ${isMobile ? 'text-xs' : 'text-sm'} text-slate-600 bg-slate-100 rounded-md hover:bg-slate-200 mobile-touch-target`}
        >
          <Copy size={isMobile ? 12 : 14} className="mr-1" />
          View Full Analysis
        </a>
      </div>
    </div>
  );

  // Main content
  const content = (
    <>
      {/* Base Claim and Reality Card */}
      <div className="w-full">
        <ClaimRealityCard 
          quote={enhancedQuote} 
          defaultExpanded={true} // Keep claim & reality expanded, but not analysis
          standalone={false} // Don't include footer, we'll add our own
          onToggleAnalysis={() => setAnalysisExpanded(!analysisExpanded)}
          analysisExpanded={analysisExpanded}
        />
      </div>

      {/* Analysis Tabs and Content */}
      {analysisExpanded && (
        <div className="w-full">
          {/* Tabs Navigation - Scrollable on mobile */}
          <div className="relative border-b border-gray-200 bg-gray-50">
            {/* Scroll indicators - only visible on mobile */}
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none md:hidden"></div>
            <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none md:hidden"></div>
            
            {/* Scrollable container */}
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex whitespace-nowrap min-w-full">
                <button
                  className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium ${activeTab === 'threats' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'} mobile-touch-target`}
                  onClick={() => setActiveTab('threats')}
                >
                  Dem. Threats
                </button>
                <button
                  className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium ${activeTab === 'tactics' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'} mobile-touch-target`}
                  onClick={() => setActiveTab('tactics')}
                >
                  Auth. Tactics
                </button>
                <button
                  className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium ${activeTab === 'historical' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'} mobile-touch-target`}
                  onClick={() => setActiveTab('historical')}
                >
                  Historical Prec.
                </button>
                <button
                  className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium ${activeTab === 'trends' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'} mobile-touch-target`}
                  onClick={() => setActiveTab('trends')}
                >
                  Dem. Trends
                </button>
                {/* Claude Analysis tab removed from frontend */}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className={`${isMobile ? 'p-3' : 'p-4'} bg-white`}>
            {loading ? (
              <div className="py-8 flex justify-center items-center text-gray-500">
                <div className={`animate-spin rounded-full ${isMobile ? 'h-6 w-6' : 'h-8 w-8'} border-b-2 border-blue-500 mr-3`}></div>
                <span className={isMobile ? 'text-sm' : ''}>Loading analysis...</span>
              </div>
            ) : error ? (
              <div className={`bg-red-50 border-l-4 border-red-500 ${isMobile ? 'p-3 text-sm' : 'p-4'} text-red-700`}>
                Error loading analysis: {error}
              </div>
            ) : (
              <>
                {/* Debug section - Dev use only */}
                {process.env.NODE_ENV !== 'production' && (
                  <div className="mb-4 p-3 bg-gray-100 text-xs overflow-auto max-h-40 rounded">
                    <h4 className="font-bold mb-1">Debug Info:</h4>
                    <div>
                      <strong>Quote ID:</strong> {quote.id}
                    </div>
                    <div>
                      <strong>Has reality text:</strong> {quote.reality_text ? 'Yes' : 'No'}
                    </div>
                    <div>
                      <strong>Framework data:</strong> {frameworkData ? 'Available' : 'Not available'}
                    </div>
                    {frameworkData && (
                      <>
                        <div>
                          <strong>Pillars:</strong> {frameworkData.pillars?.length || 0}
                        </div>
                        <div>
                          <strong>Tactics:</strong> {frameworkData.tactics?.length || 0}
                        </div>
                        <div>
                          <strong>Precedents:</strong> {frameworkData.precedents?.length || 0}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {/* Democratic Threats Tab */}
                {activeTab === 'threats' && (
                  <div className={`${isMobile ? 'mb-3' : 'mb-4'}`}>
                    <h3 className={`font-medium text-gray-800 ${isMobile ? 'text-sm mb-2' : 'mb-3'}`}>Democratic Threats</h3>
                    {frameworkData?.pillars && frameworkData.pillars.length > 0 ? (
                      <DemocracyThreatCard
                        quote={quote}
                        pillars={frameworkData.pillars}
                        defaultExpanded={true}
                        embedded={true} // Flag to indicate it's inside the enhanced card
                        isMobile={isMobile} // Pass mobile state to child component
                      />
                    ) : (
                      <div className={`bg-gray-50 ${isMobile ? 'p-3 text-sm' : 'p-4'} rounded-md border border-gray-200 text-gray-700`}>
                        <p>No democratic pillars have been associated with this quote.</p>
                        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mt-2`}>
                          This feature analyzes how claims may impact core democratic principles.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Authoritarian Tactics Tab */}
                {activeTab === 'tactics' && (
                  <div className={`${isMobile ? 'mb-3' : 'mb-4'}`}>
                    <h3 className={`font-medium text-gray-800 ${isMobile ? 'text-sm mb-2' : 'mb-3'}`}>Authoritarian Playbook</h3>
                    {frameworkData?.tactics && frameworkData.tactics.length > 0 ? (
                      <AuthoritarianPlaybook
                        quoteId={quote.id}
                        tactic={frameworkData.tactics[0]}
                        allTactics={frameworkData.tactics}
                        defaultExpanded={true}
                        embedded={true}
                        isMobile={isMobile} // Pass mobile state to child component
                      />
                    ) : (
                      <div className={`bg-gray-50 ${isMobile ? 'p-3 text-sm' : 'p-4'} rounded-md border border-gray-200 text-gray-700`}>
                        <p>No authoritarian tactics have been associated with this quote.</p>
                        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mt-2`}>
                          This feature analyzes how claims may fit into common patterns used to undermine democratic norms.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Historical Precedents Tab */}
                {activeTab === 'historical' && (
                  <div className={`${isMobile ? 'mb-3' : 'mb-4'}`}>
                    <h3 className={`font-medium text-gray-800 ${isMobile ? 'text-sm mb-2' : 'mb-3'}`}>Historical Precedents</h3>
                    {frameworkData?.precedents && frameworkData.precedents.length > 0 ? (
                      <HistoricalPrecedents
                        precedents={frameworkData.precedents}
                        defaultExpanded={true}
                        embedded={true}
                        isMobile={isMobile} // Pass mobile state to child component
                      />
                    ) : (
                      <div className={`bg-gray-50 ${isMobile ? 'p-3 text-sm' : 'p-4'} rounded-md border border-gray-200 text-gray-700`}>
                        <p>No historical precedents have been associated with this quote.</p>
                        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mt-2`}>
                          This feature connects claims to similar historical patterns in other countries.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Democracy Trends Tab - Revised to focus only on quote-specific data */}
                {activeTab === 'trends' && (
                  <div className={`${isMobile ? 'mb-3' : 'mb-4'}`}>
                    <h3 className={`font-medium text-gray-800 ${isMobile ? 'text-sm mb-2' : 'mb-3'}`}>Democracy Indices and Global Trends</h3>
                    
                    {/* Pass all relevant quote data to DemocracyInsights */}
                    <div className={`${isMobile ? 'mb-4' : 'mb-6'}`}>
                      <DemocracyInsights 
                        quoteId={quote.id}
                        pillars={frameworkData?.pillars || []}
                        indicesData={frameworkData?.indices || []}
                        serverGeneratedComparisonData={frameworkData?.globalComparisonData || null}
                        isMobile={isMobile} // Pass mobile state to child component
                      />
                    </div>
                    
                    {/* Link to additional resources */}
                    <div className={`mt-4 bg-blue-50 ${isMobile ? 'p-3' : 'p-4'} rounded-md border border-blue-100`}>
                      <h4 className={`font-medium text-gray-700 ${isMobile ? 'text-xs mb-1.5' : 'mb-2'}`}>Additional Democracy Resources</h4>
                      <ul className={`${isMobile ? 'space-y-1.5' : 'space-y-2'}`}>
                        <li>
                          <a 
                            href="https://www.idea.int/gsod" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`text-blue-600 hover:underline flex items-center ${isMobile ? 'text-xs' : 'text-sm'} mobile-touch-target`}
                          >
                            Global State of Democracy Report
                          </a>
                          <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>Comprehensive indices from International IDEA</p>
                        </li>
                        <li>
                          <a 
                            href="https://freedomhouse.org/report/freedom-world" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`text-blue-600 hover:underline flex items-center ${isMobile ? 'text-xs' : 'text-sm'} mobile-touch-target`}
                          >
                            Freedom House: Freedom in the World
                          </a>
                          <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>Annual global assessment of political rights and civil liberties</p>
                        </li>
                        <li>
                          <a 
                            href="https://www.v-dem.net" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`text-blue-600 hover:underline flex items-center ${isMobile ? 'text-xs' : 'text-sm'} mobile-touch-target`}
                          >
                            V-Dem: Varieties of Democracy
                          </a>
                          <p className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'}`}>Multidimensional democracy measurement project</p>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                
                {/* Claude Analysis tab removed from frontend */}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <CardContainer
      header={null} // No header for the main card
      footer={footer}
      className="mb-8 w-full"
      fullWidth={true} // Ensure it takes full width on all devices
    >
      {content}
    </CardContainer>
  );
};

export default EnhancedQuoteCard;